import { Column, EmptyDiv, FormContext, Row } from '@/components/SettingsLayout';
import { Text, Button, Divider, Icon, IconType, SwitchInput } from '@/ui';
import { useTranslation } from 'react-i18next';
import styles from '../styles.module.css';
import { Controller, useFormContext } from 'react-hook-form';
import AutocompleteInput, { Place } from '@/components/AutocompleteInput/AutocompleteInput';
import { CustomSelect } from '@/components';
import { Country, State } from 'country-state-city';
import { useParams } from 'react-router-dom';
import { useGetOrganizationByIdQuery, useUpdateMutation } from '@/store/slices/organizationsSlice';
import { Company } from '@/context/types';
import { skipToken } from '@reduxjs/toolkit/query';
import { SkeletonBigField, SkeletonButton } from '@/components/SkeletonLoader/SkeletonElements';
import { industriesDropDown } from './CreateOrgForm';
import toast from 'react-hot-toast';
import { useGetUser } from '@/hooks';
import { Input } from '@/ui';
import { CompanyTypes } from '@final-commerce/jwt-storage-lib';

function OrganizationProfile() {
  const { t } = useTranslation();
  const { id } = useParams();
  const { currentUser } = useGetUser();
  const {
    data: organization,
    isLoading,
    isError,
  } = useGetOrganizationByIdQuery(
    id && currentUser?.companyId ? { id, organizationId: currentUser.companyId } : skipToken,
  );
  const [update] = useUpdateMutation();

  const onSubmit = async (data: Record<string, any>) => {
    if (!organization?._id) {
      toast.error(t('Accounts.error_updating_organization'));
      return;
    }
    const newData: Partial<Company> = {
      name: data.org_name,
      builderAccess: data.builderAccess ?? false,
      scaleAccess: data.scaleAccess ?? false,
      codeAccess: data.codeAccess ?? false,
      address: {
        address1: data.address,
        city: data.city,
        postCode: data.postCode,
        country: data.country.value,
        state: data.state.value,
      },
      shipping: {
        address1: data.shipping.address,
        city: data.shipping.city,
        postCode: data.shipping.postCode,
        country: data.shipping.country.value,
        state: data.shipping.state.value,
      },
      industry: data.industry?.value ?? undefined,
    };

    update({
      id: organization?._id,
      body: newData,
    })
      .unwrap()
      .then(() => {
        toast.success(t('Accounts.organization_updated'));
      })
      .catch((error: any) => {
        if (error?.status === 409 || error?.data?.statusCode === 409) {
          toast.error(t('Accounts.organization_name_exists'));
        } else {
          toast.error(t('Accounts.error_updating_organization'));
        }
      });
  };

  if (isLoading) {
    return (
      <Column gapSize='48'>
        <Column gapSize='16'>
          <SkeletonBigField />
          <SkeletonBigField />
          <SkeletonBigField />
          <SkeletonBigField />
          <SkeletonBigField />
          <SkeletonBigField />
          <SkeletonBigField />
          <SkeletonBigField />
          <SkeletonBigField />
          <SkeletonBigField />
          <SkeletonBigField />
        </Column>
        <Column gapSize='16'>
          <Divider />
          <Row justify='justify-between'>
            <SkeletonButton />
            <SkeletonButton />
          </Row>
        </Column>
      </Column>
    );
  }

  if (isError || !organization) {
    return (
      <EmptyDiv>
        <Text size='s' weight='medium'>
          {t('Errors.no_organization')}
        </Text>
      </EmptyDiv>
    );
  }

  return (
    <FormContext
      submitHandler={onSubmit}
      defaultValues={{
        org_name: organization?.name ?? '',
        builderAccess: organization?.builderAccess ?? false,
        scaleAccess: organization?.scaleAccess ?? false,
        codeAccess: organization?.codeAccess ?? false,
        // industry: organization.industry,
        city: organization?.address?.city ?? '',
        postCode: organization?.address?.postCode ?? '',
        address: organization?.address?.address1 ?? '',
        shipping: {
          address: (organization?.shipping?.address1 || organization?.address?.address1) ?? '',
          city: organization?.shipping?.city ?? '',
          postCode: organization?.shipping?.postCode ?? '',
          country: organization?.shipping?.country
            ? {
                value: organization?.shipping?.country,
                label: Country.getCountryByCode(organization?.shipping?.country)?.name,
              }
            : {},
          state: {
            value: organization?.shipping?.state,
            label: State.getStateByCodeAndCountry(
              organization?.shipping?.state,
              organization?.shipping?.country,
            )?.name,
          },
        },
        country: {
          value: organization?.address?.country,
          label: Country.getCountryByCode(organization?.address?.country)?.name,
        },
        state: {
          value: organization?.address?.state,
          label: State.getStateByCodeAndCountry(
            organization?.address?.state,
            organization?.address?.country,
          )?.name,
        },
        industry: organization?.industry
          ? {
              value: organization?.industry,
              label: t(`Accounts.${organization?.industry}`),
            }
          : {
              value: industriesDropDown[0]?.value,
              label: t(`Accounts.${industriesDropDown[0]?.label}`),
            },
      }}
    >
      <Column gapSize='48'>
        <FormFields organization={organization} />
        <Column gapSize='16'>
          <Divider />
          <Row justify='justify-end'>
            <Button variant='primary' type='submit'>
              {t('Shared.save_changes')}
            </Button>
          </Row>
        </Column>
      </Column>
    </FormContext>
  );
}

export default OrganizationProfile;

type Props = {
  organization: Company;
};

const FormFields = ({ organization }: Props) => {
  const { t } = useTranslation();
  const { company } = useGetUser();
  const {
    register,
    formState: { errors },
    control,
    watch,
    setValue,
  } = useFormContext();
  const chosenCountry = watch('country');
  const address = watch('address');
  const shippingAddress = watch('shipping.address');

  const handlePlaceSelect = (place: Place, mode: 'billing' | 'shipping' = 'billing') => {
    const formatted_address = place.formatted_address;

    if (mode === 'billing') {
      setValue('address', formatted_address);
    }

    if (mode === 'shipping') {
      setValue('shipping.address', formatted_address);
    }

    const cityComponent = place.address_components?.find(
      (component) =>
        component.types.includes('locality') ||
        component.types.includes('administrative_area_level_1') ||
        component.types.includes('administrative_area_level_2') ||
        component.types.includes('administrative_area_level_3'),
    );
    if (cityComponent) {
      setValue(mode === 'billing' ? 'city' : 'shipping.city', cityComponent.long_name);
      setValue('shipping.city', cityComponent.long_name);
    }
    const postalCodeComponent = place.address_components.find((component) =>
      component.types.includes('postal_code'),
    );
    if (postalCodeComponent) {
      setValue(
        mode === 'billing' ? 'postCode' : 'shipping.postCode',
        postalCodeComponent.long_name,
      );
    }
    const stateComponent = place.address_components.find((component) =>
      component.types.includes('administrative_area_level_1'),
    );
    if (stateComponent && chosenCountry?.value) {
      const newState = State.getStateByCodeAndCountry(
        stateComponent.short_name,
        chosenCountry.value,
      );
      if (newState) {
        setValue(mode === 'billing' ? 'state' : 'shipping.state', {
          value: newState.isoCode,
          label: newState.name,
        });
      }
    }
    const countryComponent = place.address_components.find((component) =>
      component.types.includes('country'),
    );
    if (countryComponent) {
      const countryValue = countryComponent.short_name;
      const countryLabel = countryComponent.long_name;
      setValue(mode === 'billing' ? 'country' : 'shipping.country', {
        value: countryValue,
        label: countryLabel,
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setValue('profileImage', file);
    }
  };

  return (
    <div className={styles.profileForm}>
      <Row gapSize='24' justify='justify-between'>
        <Input
          label={t('Accounts.organization_name')}
          {...register('org_name', {
            required: t('Form.required.org_name'),
          })}
        />
      </Row>
      <Row gapSize='24' justify='justify-between'>
        <Controller
          name={'industry'}
          control={control}
          rules={{ required: t('Form.required.industry') }}
          render={({ field }) => {
            const options = industriesDropDown.map((industry) => ({
              value: industry.value,
              label: t(`Accounts.${industry.label}`),
            }));
            return (
              <CustomSelect
                options={options}
                isSearchable={true}
                variant='full'
                selectSize='large'
                field={field}
                inputId='industry'
              />
            );
          }}
        />
      </Row>
      <Text size='s' weight='semi-bold'>
        {t('Accounts.billing_address')}
      </Text>

      <AutocompleteInput
        size='large'
        onChange={(place) => handlePlaceSelect(place, 'billing')}
        placeholder={t('Form.placeholders.address')}
        value={address as string}
        defaultValue={address}
      />

      <Row gapSize='24' justify='justify-between'>
        <Input
          size='large'
          label={t('Shared.city')}
          placeholder={t('Form.placeholders.city')}
          {...register('city')}
        />
        <Controller
          name={'country'}
          control={control}
          render={({ field }) => {
            const options = Country.getAllCountries().map((country) => ({
              value: country?.isoCode,
              label: country?.name,
            }));

            return (
              <>
                <CustomSelect
                  label={t('Shared.country')}
                  options={options}
                  selectSize='large'
                  isSearchable={true}
                  field={field}
                  inputId='country'
                  onChange={handleFileChange}
                />
                {errors?.state && (
                  <Text size='xxs' weight='light-medium' color='red'>
                    {errors.state?.message as string}
                  </Text>
                )}
              </>
            );
          }}
        />
        <Controller
          name={'state'}
          control={control}
          render={({ field }) => {
            const selectedCountry = chosenCountry?.value || organization?.address?.country;
            const options = State.getStatesOfCountry(selectedCountry).map((state) => ({
              value: state.isoCode,
              label: state.name,
            }));

            return (
              <>
                <CustomSelect
                  label={t('Shared.state')}
                  options={options}
                  selectSize='large'
                  isSearchable={true}
                  field={field}
                  inputId='state-input'
                />
                {errors?.state && (
                  <Text size='xxs' weight='light-medium' color='red'>
                    {errors.state?.message as string}
                  </Text>
                )}
              </>
            );
          }}
        />
        <Input
          label={t('Shared.postal_code')}
          size='large'
          placeholder={t('Form.placeholders.postal_code')}
          {...register('postCode')}
        />
      </Row>

      <Text size='s' weight='semi-bold'>
        {t('Accounts.shipping_address')}
      </Text>
      <AutocompleteInput
        size='large'
        onChange={(place) => handlePlaceSelect(place, 'shipping')}
        placeholder={t('Form.placeholders.address')}
        value={shippingAddress as string}
        defaultValue={shippingAddress}
      />

      <Row gapSize='24' justify='justify-between'>
        <Input
          size='large'
          label={t('Shared.city')}
          placeholder={t('Form.placeholders.city')}
          {...register('shipping.city')}
        />

        <Controller
          name={'shipping.country'}
          control={control}
          render={({ field }) => {
            const options = Country.getAllCountries().map((country) => ({
              value: country?.isoCode,
              label: country?.name,
            }));

            return (
              <>
                <CustomSelect
                  label={t('Shared.country')}
                  options={options}
                  selectSize='large'
                  isSearchable={true}
                  field={field}
                  inputId='shipping-country'
                  onChange={handleFileChange}
                />
                {errors?.state && (
                  <Text size='xxs' weight='light-medium' color='red'>
                    {errors.state?.message as string}
                  </Text>
                )}
              </>
            );
          }}
        />

        <Controller
          name={'shipping.state'}
          control={control}
          render={({ field }) => {
            const selectedCountry =
              watch('shipping.country')?.value ?? organization?.shipping?.country;
            const options = State.getStatesOfCountry(selectedCountry).map((state) => ({
              value: state.isoCode,
              label: state.name,
            }));

            return (
              <>
                <CustomSelect
                  label={t('Shared.state')}
                  options={options}
                  selectSize='large'
                  isSearchable={true}
                  field={field}
                  inputId='shipping-state-input'
                />
                {errors?.state && (
                  <Text size='xxs' weight='light-medium' color='red'>
                    {errors.state?.message as string}
                  </Text>
                )}
              </>
            );
          }}
        />

        <Input
          size='large'
          label={t('Shared.postal_code')}
          placeholder={t('Form.placeholders.postal_code')}
          {...register('shipping.postCode')}
        />
      </Row>
      {organization.type === CompanyTypes.ORGANIZATION && company?.type === CompanyTypes.ROOT && (
        <>
          <Text size='s' weight='semi-bold'>
            {t('Accounts.tools_access')}
          </Text>
          <div className={styles.toolsAccessContainer}>
            <div className={styles.toolsAccessItem}>
              <Column>
                <Icon type={IconType.CodeLogo} width={75} height={32} />
                <Text size='s' weight='light-medium'>
                  {t('Accounts.code_access_description')}
                </Text>
              </Column>
              <Controller
                name={'codeAccess'}
                control={control}
                render={({ field }) => {
                  return <SwitchInput field={field} checked={Boolean(field.value)} />;
                }}
              />
            </div>
            <Divider />

            <div className={styles.toolsAccessItem}>
              <Column>
                <Icon type={IconType.ScaleLogo} width={75} height={32} />
                <Text size='s' weight='light-medium'>
                  {t('Accounts.scale_access_description')}
                </Text>
              </Column>
              <Controller
                name={'scaleAccess'}
                control={control}
                render={({ field }) => {
                  return <SwitchInput field={field} checked={Boolean(field.value)} />;
                }}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};
